import { useState, type FormEvent } from 'react';
import { ErrorState, Loading } from '../../components/States';
import { useAdminCourseCurriculum, useCreateAdminCourseSection, useCreateAdminLesson, useRefreshLessonVideoStatus, useUpdateAdminLesson, useUploadLessonVideo } from '../../hooks/api-hooks';
import type { AdminCourseSection } from '../../lib/types';

function SectionEditor({ courseId, section }: { courseId: string; section: AdminCourseSection }) {
  const createLesson = useCreateAdminLesson(courseId, section.id);
  const uploadVideo = useUploadLessonVideo(courseId);
  const refreshVideo = useRefreshLessonVideoStatus(courseId);
  const updateLesson = useUpdateAdminLesson(courseId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [preview, setPreview] = useState(false);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    createLesson.mutate({ title, description: description || undefined, position: section.lessons.length, isPreview: preview }, {
      onSuccess: () => { setTitle(''); setDescription(''); setPreview(false); },
    });
  }

  return <article className="curriculum-section">
    <h3>{section.position + 1}. {section.title}</h3>
    <div className="curriculum-admin-lessons">
      {section.lessons.map((lesson) => <div key={lesson.id} className="curriculum-admin-lesson">
        <div><strong>{lesson.position + 1}. {lesson.title}</strong><small>{lesson.isPreview ? 'Học thử · ' : ''}{lesson.videoStatus === 'READY' ? 'Video sẵn sàng' : lesson.videoStatus === 'PROCESSING' ? 'Video đang xử lý' : lesson.videoStatus === 'ERROR' ? 'Video lỗi' : 'Chưa có video'}</small></div>
        <button type="button" className="btn btn-small btn-quiet" disabled={updateLesson.isPending} onClick={() => updateLesson.mutate({ lessonId: lesson.id, input: { isPreview: !lesson.isPreview } })}>{lesson.isPreview ? 'Tắt học thử' : 'Cho học thử'}</button>
        <label className="file-button">{uploadVideo.isPending ? 'Đang tải…' : 'Tải video'}<input type="file" accept="video/*" disabled={uploadVideo.isPending} onChange={(event) => { const file = event.target.files?.[0]; if (file) uploadVideo.mutate({ lessonId: lesson.id, file }); }} /></label>
        {lesson.videoUid && <button type="button" className="btn btn-small btn-quiet" disabled={refreshVideo.isPending} onClick={() => refreshVideo.mutate(lesson.id)}>Kiểm tra video</button>}
      </div>)}
    </div>
    <form className="admin-inline-form" onSubmit={submit}>
      <input required value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Tên bài học mới" />
      <input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Mô tả ngắn" />
      <label className="admin-checkbox"><input type="checkbox" checked={preview} onChange={(event) => setPreview(event.target.checked)} /> Học thử</label>
      <button className="btn btn-small" disabled={createLesson.isPending}>Thêm bài</button>
    </form>
    {(createLesson.error || uploadVideo.error || refreshVideo.error || updateLesson.error) && <ErrorState error={(createLesson.error || uploadVideo.error || refreshVideo.error || updateLesson.error) as Error} />}
  </article>;
}

export function AdminCourseContentPanel({ courseId, onClose }: { courseId: string; onClose: () => void }) {
  const curriculum = useAdminCourseCurriculum(courseId);
  const createSection = useCreateAdminCourseSection(courseId);
  const [title, setTitle] = useState('');
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    createSection.mutate({ title, position: curriculum.data?.sections.length ?? 0 }, { onSuccess: () => setTitle('') });
  }
  return <section className="admin-content-editor">
    <header><div><h2>Nội dung {curriculum.data?.title}</h2><p>Tạo chương, bài học và upload video thẳng lên Cloudflare Stream.</p></div><button type="button" className="btn btn-small btn-quiet" onClick={onClose}>Đóng</button></header>
    {curriculum.isPending && <Loading />}{curriculum.error && <ErrorState error={curriculum.error} />}
    {curriculum.data?.sections.map((section) => <SectionEditor key={section.id} courseId={courseId} section={section} />)}
    <form className="admin-inline-form" onSubmit={submit}><input required value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Tên chương mới" /><button className="btn btn-small" disabled={createSection.isPending}>Thêm chương</button></form>
    {createSection.error && <ErrorState error={createSection.error} />}
  </section>;
}
